import * as React from "react";

import {
  deactivatePushSubscription,
  getPushPublicKey,
  registerPushSubscription,
} from "@/features/push/services/push-api";
import {
  getNotificationPermission,
  isPushSupported,
  resolveDeviceLabel,
  urlBase64ToUint8Array,
} from "@/features/push/lib/push-utils";

export type PushDeviceStatus = "loading" | "unsupported" | "blocked" | "enabled" | "disabled";

/** Specific failure reason so the UI can explain what to do, instead of a generic message. */
export type PushErrorReason = "not-configured" | "no-service-worker" | "generic" | null;

/** Outcome of an enable() attempt, so callers (the prompt banner) can react instead of guessing from state. */
export type PushEnableReason = "blocked" | "unsupported" | NonNullable<PushErrorReason> | null;
export type PushEnableOutcome = { ok: true } | { ok: false; reason: PushEnableReason };

type UsePushSubscriptionResult = {
  status: PushDeviceStatus;
  isBusy: boolean;
  errorReason: PushErrorReason;
  enable: () => Promise<PushEnableOutcome>;
  disable: () => Promise<void>;
};

async function resolveVapidPublicKey(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv.trim();
  try {
    return await getPushPublicKey();
  } catch {
    return null;
  }
}

/**
 * Resolve an active service-worker registration without hanging. `serviceWorker.ready`
 * never resolves when no SW is registered (e.g. dev mode with the PWA SW disabled), so we
 * race it against an existing registration + a timeout.
 */
async function resolveActiveRegistration(timeoutMs = 5000): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active) return existing;

  const ready: Promise<ServiceWorkerRegistration | null> = navigator.serviceWorker.ready;
  const timeout = new Promise<ServiceWorkerRegistration | null>((resolve) =>
    setTimeout(() => resolve(existing ?? null), timeoutMs),
  );
  return Promise.race([ready, timeout]);
}

/**
 * Device-level push enable/disable for the current browser. Permission is only requested
 * in response to a user action (enable()). When permission is denied we surface "blocked"
 * and never re-prompt automatically.
 */
export function usePushSubscription(): UsePushSubscriptionResult {
  const [status, setStatus] = React.useState<PushDeviceStatus>("loading");
  const [isBusy, setIsBusy] = React.useState(false);
  const [errorReason, setErrorReason] = React.useState<PushErrorReason>(null);

  const refresh = React.useCallback(async () => {
    if (!isPushSupported()) {
      setStatus("unsupported");
      return;
    }

    const permission = getNotificationPermission();
    if (permission === "denied") {
      setStatus("blocked");
      return;
    }

    try {
      // getRegistration() resolves immediately (null when no SW is registered) — unlike
      // serviceWorker.ready, which never resolves when the PWA SW is disabled (e.g. dev mode).
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setStatus("disabled");
        return;
      }
      const subscription = await registration.pushManager.getSubscription();
      setStatus(subscription ? "enabled" : "disabled");
    } catch {
      setStatus("disabled");
    }
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = React.useCallback(async (): Promise<PushEnableOutcome> => {
    setErrorReason(null);
    if (!isPushSupported()) {
      setStatus("unsupported");
      return { ok: false, reason: "unsupported" };
    }

    setIsBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus(permission === "denied" ? "blocked" : "disabled");
        // "denied" is a real blocked state to surface; "default" just means they dismissed the native prompt.
        return { ok: false, reason: permission === "denied" ? "blocked" : null };
      }

      const publicKey = await resolveVapidPublicKey();
      if (!publicKey) {
        // No VAPID key from VITE_VAPID_PUBLIC_KEY or the API — push isn't configured.
        setErrorReason("not-configured");
        return { ok: false, reason: "not-configured" };
      }

      const registration = await resolveActiveRegistration();
      if (!registration) {
        // No active service worker (e.g. dev with the PWA SW disabled — set VITE_ENABLE_DEV_SW=true).
        setErrorReason("no-service-worker");
        await refresh();
        return { ok: false, reason: "no-service-worker" };
      }
      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        }));

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        setErrorReason("generic");
        return { ok: false, reason: "generic" };
      }

      await registerPushSubscription({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        deviceLabel: resolveDeviceLabel(),
      });

      setStatus("enabled");
      return { ok: true };
    } catch {
      setErrorReason("generic");
      await refresh();
      return { ok: false, reason: "generic" };
    } finally {
      setIsBusy(false);
    }
  }, [refresh]);

  const disable = React.useCallback(async () => {
    setErrorReason(null);
    if (!isPushSupported()) return;

    setIsBusy(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = registration ? await registration.pushManager.getSubscription() : null;
      if (subscription) {
        await deactivatePushSubscription(subscription.endpoint).catch(() => undefined);
        await subscription.unsubscribe().catch(() => undefined);
      }
      setStatus("disabled");
    } catch {
      setErrorReason("generic");
      await refresh();
    } finally {
      setIsBusy(false);
    }
  }, [refresh]);

  return { status, isBusy, errorReason, enable, disable };
}
